
import React, { useState } from 'react';

const TABS = [
  {
    id: 'prescripcion',
    role: 'Médico Oncólogo',
    title: 'Precisión en la Prescripción',
    desc: 'Motor de cálculo oncológico avanzado. Gestione dosis basadas en superficie corporal (BSA), AUC (Fórmula de Calvert), peso real/ideal o dosis fijas con total flexibilidad.',
    icon: 'fa-user-md',
    features: ['Ajuste de dosis personalizado y alertas', 'Visualización de historial de ciclos', 'Cálculos de BSA y AUC automatizados'],
    color: 'bg-medical',
    image: 'https://raw.githubusercontent.com/jcalcedo/webpageimages/main/Captura%20de%20pantalla%20prescripcion%202.png' 
  },
  {
    id: 'farmacia',
    role: 'Farmacia Oncológica',
    title: 'Seguridad en la Preparación',
    desc: 'Control estricto de mezclas e instrucciones de dilución. OncoFHIR aplica criterios de redondeo inteligente para optimización de viales y seguridad clínica.',
    icon: 'fa-flask',
    features: ['Redondeo de dosis (±10%) según HOPA/ASCO', 'Hoja de preparación digital', 'Validación farmacéutica de 2 pasos'],
    color: 'bg-onco',
    image: 'https://raw.githubusercontent.com/jcalcedo/webpageimages/main/Captura%20de%20pantalla%20preparacion%202.png',
    quote: 'El redondeo de agentes biológicos y citotóxicos dentro del 10% de la dosis ordenada es recomendado por HOPA, NCCN y ASCO, ya que no altera la seguridad o efectividad de la terapia.'
  },
  {
    id: 'administracion',
    role: 'Enfermería',
    title: 'Validación con Movilidad Total',
    desc: 'Registre la administración al pie de cama usando PC, Tablet o Smartphone. Valide la mezcla correcta mediante la lectura del código QR de la etiqueta.',
    icon: 'fa-clipboard-check',
    features: ['Lectura de QR para validación de mezcla', 'Uso en dispositivos móviles y tablets', 'Control de tiempos de infusión mCODE'],
    color: 'bg-fhir',
    image: 'https://raw.githubusercontent.com/jcalcedo/webpageimages/main/Captura%20de%20pantalla%20enfermeria.png'
  }
];

const ProductShowcase: React.FC = () => {
  const [activeTab, setActiveTab] = useState(TABS[0]);

  return (
    <section className="py-24 bg-slate-50 overflow-hidden" id="demo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-fhir font-black text-sm uppercase tracking-widest mb-4 inline-block tracking-[0.3em]">Software Real • Estándares de Interoperabilidad</span>
          <h2 className="text-3xl md:text-5xl font-black text-dark mb-6 italic leading-tight">
            Explore el <span className="text-medical not-italic">Ecosistema OncoFHIR Prescribe</span>
          </h2>
          <p className="text-lg text-gray-500 font-medium max-w-2xl mx-auto leading-relaxed">
            Nuestra plataforma está fundamentada en la iniciativa <span className="text-dark font-bold">FHIR-based NCCN chemotherapy orders IG</span>, asegurando flujos clínicos robustos.
          </p>
        </div>

        {/* Selector de Roles */}
        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab)}
              className={`px-8 py-5 rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all border-none cursor-pointer flex items-center gap-3 shadow-sm ${
                activeTab.id === tab.id 
                ? `${tab.color} text-white shadow-xl scale-105 -translate-y-1` 
                : 'bg-white text-gray-400 hover:text-medical hover:shadow-md'
              }`}
            >
              <i className={`fas ${tab.icon} text-sm`}></i>
              {tab.role}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="animate-in fade-in slide-in-from-left duration-700">
            <div className={`inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest ${activeTab.id === 'prescripcion' ? 'text-medical' : activeTab.id === 'farmacia' ? 'text-onco' : 'text-fhir'} mb-6`}>
               <i className={`fas ${activeTab.icon}`}></i> Módulo Especializado
            </div>
            <h3 className="text-3xl font-black text-dark mb-6 leading-tight italic">
              {activeTab.title}
            </h3>
            <p className="text-gray-500 font-medium text-lg mb-8 leading-relaxed">
              {activeTab.desc}
            </p>
            
            <div className="space-y-4 mb-10">
              {activeTab.features.map((f, i) => (
                <div key={i} className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-slate-100 shadow-sm transition-transform hover:translate-x-2">
                  <div className={`w-10 h-10 rounded-xl ${activeTab.color} text-white flex items-center justify-center text-sm shadow-inner`}>
                    <i className="fas fa-check"></i>
                  </div>
                  <span className="text-sm font-bold text-dark uppercase tracking-tight">{f}</span>
                </div>
              ))}
            </div>

            {activeTab.quote && (
              <div className="mb-10 p-6 bg-onco/5 border-l-4 border-onco rounded-r-2xl italic">
                <p className="text-xs text-onco font-bold leading-relaxed mb-2">
                  <i className="fas fa-info-circle mr-2"></i> Estándar de Farmacia Oncológica:
                </p>
                <p className="text-[11px] text-gray-600 font-medium leading-relaxed">
                  "{activeTab.quote}"
                </p>
                <p className="text-[9px] text-gray-400 mt-2 uppercase font-black tracking-widest">
                  Fuente: HOPA / ASCO / NCCN Guidelines
                </p>
              </div>
            )}

            <div className="p-8 bg-slate-100/50 rounded-[2.5rem] border border-dashed border-slate-300">
              <div className="flex flex-col items-center gap-4 text-center">
                <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.2em] mb-2">Fundamentación en Estándares</p>
                <div className="flex flex-wrap justify-center gap-6 opacity-60">
                  <span className="text-[10px] font-black tracking-tighter">HL7® FHIR® R4</span>
                  <span className="text-[10px] font-black tracking-tighter">mCODE™ IG</span>
                  <span className="text-[10px] font-black tracking-tighter">NCCN® FHIR IG FOUNDATION</span>
                </div>
              </div>
            </div>
          </div>

          <div className="relative group animate-in fade-in zoom-in duration-700">
            <div className="relative mx-auto">
              <div className="bg-dark rounded-[2.5rem] p-2.5 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] border-[8px] border-slate-800 relative overflow-hidden ring-1 ring-white/10">
                <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-white/10 to-transparent pointer-events-none z-10"></div>
                <div className="bg-slate-900 aspect-[16/10] overflow-hidden rounded-[1.8rem] relative group/img flex items-center justify-center">
                  <img 
                    key={activeTab.id}
                    src={activeTab.image} 
                    alt={`Interfaz real OncoFHIR Prescribe - ${activeTab.role}`}
                    className="w-full h-full object-cover sm:object-contain transition-all duration-1000 group-hover/img:scale-[1.02]"
                    loading="lazy"
                  />
                  <div className="absolute top-4 right-4 z-20">
                    <div className="bg-dark/60 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                      <span className="text-[9px] font-black text-white uppercase tracking-widest">Live Capture</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="h-5 w-[106%] -ml-[3%] bg-gradient-to-b from-slate-700 to-slate-900 rounded-b-[2rem] shadow-2xl relative z-0">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1.5 bg-slate-950 rounded-b-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
